<template>
	<Sheet v-if="permitted" v-model:open="sheetOpen">
	<SheetTrigger as-child>
	<Button>Add Group</Button>
	</SheetTrigger>

	<SheetContent side="right" class="w-1/2">
	<SheetHeader>
	<SheetTitle>Create Group</SheetTitle>
	<SheetDescription>
	Add a new group.
	</SheetDescription>
	</SheetHeader>
	<div class="p-3 flex flex-col space-y-4">
		<Label>Name</Label>
		<Input v-model="name" placeholder="A name for your group"/>
		<Label>Description</Label>
		<Input v-model="desc" placeholder="Description or purpose of the group"/>
		<Label>Parent Group</Label>
		<TreeViewSelect :groupTree="groupTree"  v-model="parentGroup"/>
		<div class="w-full flex justify-end">
			<Button v-if="name!=''" :disabled="creating" @click="handleCreate">
				<Loader2 v-if="creating" class="h-4 w-4 mr-2 animate-spin" />
				{{ creating ? 'Creating...' : 'Create' }}
			</Button>
		</div>
	</div>
	</SheetContent>
	</Sheet>

</template>

<script setup lang="ts">
import TreeViewSelect from '@/components/TreeViewSelect.vue'
import { ref, onMounted, Ref, inject } from 'vue'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, } from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Loader2 } from 'lucide-vue-next'
import { toast } from 'vue-sonner'

import { useGroups } from '@/services/Groups'
import { Member } from '@/types/Member'
import permissions from '@/services/permissions'

const { getGroupTree, groupTree, createGroup } = useGroups()
const { checkPermission } = permissions()
const vhostId = inject<Ref<string> | null>('vhostId', null)
const userInfo = inject<Ref<Member> | null>('userInfo', null)
const name = ref('')
const desc = ref('')
const permitted = ref(false)
const parentGroup = ref('0')
const creating = ref(false)
const sheetOpen = ref(false)

const handleCreate = async () => {
	if (!name.value || !vhostId?.value) return
	creating.value = true
	try {
		const additionalInfo: Record<string, string> = {}
		if (desc.value) additionalInfo.description = desc.value
		if (parentGroup.value && parentGroup.value !== '0') {
			additionalInfo.parentGroup = parentGroup.value
		}

		const result = await createGroup(Number(vhostId.value), name.value, additionalInfo)
		if (result) {
			toast('Group Created', { description: `"${name.value}" has been created successfully.` })
			name.value = ''
			desc.value = ''
			parentGroup.value = '0'
			sheetOpen.value = false
			await getGroupTree(vhostId.value)
		} else {
			toast('Error', { description: 'Failed to create group.' })
		}
	} catch (error) {
		console.error('Failed to create group:', error)
		toast('Error', { description: 'Failed to create group.' })
	} finally {
		creating.value = false
	}
}

onMounted(async () => {
	if (vhostId && userInfo?.value?.permissions) {
		const perms = userInfo.value.permissions[vhostId.value]
		if (checkPermission(perms, 'groups.createGroup')) {
			permitted.value = true
			await getGroupTree(vhostId.value)
		}
	}
})
</script>
